<template>
  <div />
</template>

<script>

/**
 * Vue: hero-section (hero-section)
 * -----------------------------------------------------------------------------
 * Hero Section.
 * - Limited interactivity section.
 *
 * @param {Boolean} [autoplay] - Autoplay setting.
 * @param {String} liquid - Liquid settings.
 * @param {String} sectionContent - Media type.
 *
 */
import { mapState } from 'vuex'

export default {
  name: 'HeroSection',

  props: {
    autoplay: {
      type: Boolean,
      default: false,
    },
    liquid: {
      type: Object,
      default: () => ({
        blockId: '',
        sectionId: '',
      }),
    },
    sectionContent: {
      type: String,
      default: 'image',
    },
  },

  data() {
    return {
      muted: this.autoplay,
      nodeSelectors: {
        container: document.querySelector(`[js-hero="container-${this.liquid.blockId}"]`),
        controls: document.querySelector(`[js-hero="controls-${this.liquid.blockId}"]`),
        mute: document.querySelector(`[js-hero="mute-${this.liquid.blockId}"]`),
        pause: document.querySelector(`[js-hero="pause-${this.liquid.blockId}"]`),
        play: document.querySelector(`[js-hero="play-${this.liquid.blockId}"]`),
        videoDesktop: document.querySelector(`[js-hero="video-desktop-${this.liquid.blockId}"]`),
        videoMobile: document.querySelector(`[js-hero="video-mobile-${this.liquid.blockId}"]`),
      },
      state: this.autoplay ? 'playing' : 'paused',
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),

    /**
     * Compute video element.
     * @returns {HTMLElement}
     */
    videoElement() {
      return this.isMobile
        ? this.nodeSelectors.videoMobile
        : this.nodeSelectors.videoDesktop
    },
  },

  watch: {

    /**
     * Watch isMobile to pause other breakpoint's video.
     */
    isMobile() {
      if (this.sectionContent !== 'video') {
        return
      }

      if (this.isMobile) {
        if (this.state === 'playing') {
          this.nodeSelectors.videoMobile.play()
        }

        this.nodeSelectors.videoMobile.muted = this.muted

        this.nodeSelectors.videoDesktop.pause()
        return
      }

      if (this.state === 'playing') {
        this.nodeSelectors.videoDesktop.play()
      }

      this.nodeSelectors.videoDesktop.muted = this.muted

      this.nodeSelectors.videoMobile.pause()
    },

    /**
     * Watch muted to toggle sound.
     */
    muted() {
      if (this.muted) {
        this.videoElement.muted = true
        return
      }

      this.videoElement.muted = false
    },

    /**
     * Watch state to update active classes.
     */
    state() {
      if (this.state === 'playing') {
        this.nodeSelectors.container.classList.add(this.$class('active'))
        return
      }

      this.nodeSelectors.container.classList.remove(this.$class('active'))
    },
  },

  mounted() {
    if (this.sectionContent !== 'video') {
      return
    }

    this.setEventListeners()

    if (this.autoplay) {
      this.handlePlay()
      return
    }

    this.muted = false
  },

  methods: {

    /**
     * Set event listeners.
     */
    setEventListeners() {
      this.nodeSelectors.play?.addEventListener('click', () => {
        this.handlePlay()
      })

      this.nodeSelectors.pause?.addEventListener('click', () => {
        this.handlePause()
      })

      this.nodeSelectors.mute?.addEventListener('click', () => {
        this.handleMute()
      })
    },

    /**
     * Handle mute.
     */
    handleMute() {
      this.muted = !this.muted
    },

    /**
     * Handle play.
     */
    handlePlay() {
      this.videoElement.play()
      this.state = 'playing'
    },

    /**
     * Handle pause
     */
    handlePause() {
      this.videoElement.pause()
      this.state = 'paused'
    },
  },
}
</script>

<style lang="scss">
@import './hero-section';
</style>
